import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpService } from './http.service';
import { LoggerService } from './logger.service';
import { LanguageService } from './language.service';

/**
 * Chat message structure
 */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

/**
 * Help document for display
 */
export interface HelpDocument {
  id: string;
  title: string;
  summary: string;
  url: string;
  category: string;
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

/**
 * Chat API response data
 */
interface ChatResponseData {
  message: string;
  conversation_id: string;
}

/**
 * Documents API response data
 */
interface DocumentsResponseData {
  documents: Record<string, HelpDocument[]>;
}

/**
 * Search API response data
 */
interface SearchResponseData {
  results: HelpDocument[];
  count: number;
}

/**
 * Service for interacting with the Concierge AI chatbot.
 * Handles chat messages, conversation history, and help document retrieval.
 */
@Injectable({ providedIn: 'root' })
export class ConciergeService {
  private conversationId: string | null = null;
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private isOpenSubject = new BehaviorSubject<boolean>(false);

  /** Observable of all chat messages */
  public messages$ = this.messagesSubject.asObservable();

  /** Observable of loading state */
  public isLoading$ = this.isLoadingSubject.asObservable();

  /** Observable of chat widget open state */
  public isOpen$ = this.isOpenSubject.asObservable();

  constructor(
    private http: HttpService,
    private logger: LoggerService,
    private languageService: LanguageService
  ) {}

  /**
   * Send a message to the AI concierge.
   */
  sendMessage(message: string): Observable<ChatResponseData> {
    const locale = this.languageService.getCurrentLanguage() || 'en';

    this.isLoadingSubject.next(true);

    return this.http.post<ApiResponse<ChatResponseData>>('v2/concierge/chat', {
      message,
      conversation_id: this.conversationId,
      locale
    }).pipe(
      map(response => {
        this.conversationId = response.data.conversation_id;
        this.isLoadingSubject.next(false);
        return response.data;
      }),
      catchError(error => {
        this.isLoadingSubject.next(false);
        this.logger.error('Concierge chat failed', error);
        return throwError(() => new Error('Failed to get response from assistant'));
      })
    );
  }

  /**
   * Add a user message to the conversation.
   */
  addUserMessage(content: string): void {
    const messages = this.messagesSubject.value;
    messages.push({
      role: 'user',
      content,
      timestamp: new Date()
    });
    this.messagesSubject.next([...messages]);
  }

  /**
   * Add an assistant message to the conversation.
   */
  addAssistantMessage(content: string): void {
    const messages = this.messagesSubject.value;
    messages.push({
      role: 'assistant',
      content,
      timestamp: new Date()
    });
    this.messagesSubject.next([...messages]);
  }

  /**
   * Clear the current conversation.
   */
  clearConversation(): Observable<void> {
    if (!this.conversationId) {
      this.messagesSubject.next([]);
      return of(undefined);
    }

    return this.http.post<ApiResponse<{ success: boolean }>>('v2/concierge/clear', {
      conversation_id: this.conversationId
    }).pipe(
      map(() => {
        this.conversationId = null;
        this.messagesSubject.next([]);
      }),
      catchError(error => {
        this.logger.error('Failed to clear conversation', error);
        // Still clear locally even if API fails
        this.conversationId = null;
        this.messagesSubject.next([]);
        return of(undefined);
      })
    );
  }

  /**
   * Get all help documents grouped by category.
   */
  getHelpDocuments(): Observable<Record<string, HelpDocument[]>> {
    const locale = this.languageService.getCurrentLanguage() || 'en';

    return this.http.get<ApiResponse<DocumentsResponseData>>(`v2/concierge/documents?locale=${locale}`).pipe(
      map(response => response.data.documents),
      catchError(error => {
        this.logger.error('Failed to fetch help documents', error);
        return throwError(() => new Error('Failed to load help documents'));
      })
    );
  }

  /**
   * Search help documents.
   */
  searchDocuments(query: string): Observable<HelpDocument[]> {
    const locale = this.languageService.getCurrentLanguage() || 'en';

    return this.http.get<ApiResponse<SearchResponseData>>(`v2/concierge/search?query=${encodeURIComponent(query)}&locale=${locale}`).pipe(
      map(response => response.data.results),
      catchError(error => {
        this.logger.error('Document search failed', error);
        return throwError(() => new Error('Search failed'));
      })
    );
  }

  /**
   * Get current messages.
   */
  getMessages(): ChatMessage[] {
    return this.messagesSubject.value;
  }

  /**
   * Check if chat widget is open.
   */
  isOpen(): boolean {
    return this.isOpenSubject.value;
  }

  /**
   * Open the chat widget.
   */
  openChat(): void {
    this.isOpenSubject.next(true);
  }

  /**
   * Close the chat widget.
   */
  closeChat(): void {
    this.isOpenSubject.next(false);
  }

  /**
   * Toggle the chat widget.
   */
  toggleChat(): void {
    this.isOpenSubject.next(!this.isOpenSubject.value);
  }
}
